// Talking Family Tree — API layer.
//
// Reuses the shared request helpers (utils/api): Token auth, the priority queue,
// JSON:API normalization and the `getSWR` low-priority fetcher — exactly like the
// rest of the app. See TALKING_FAMILY_TREE_PLAN.md for the endpoint mapping.
//
// SCOPING ASSUMPTION: the backend docs write `/family_tree` and `/family_members`
// without a prefix, but the documented `book_pages` endpoint is nested under
// `child_journals/:id`. Since the feature is per-baby (reorder = "position in the
// baby's list"), we nest family endpoints under the child journal too. This is the
// single place to change if the real routes are account-level.

import useSWR from 'swr';
import {
  get, post, patch, destroy, getSWR,
} from 'utils/api';

const base = (journalId) => `/v3/child_journals/${journalId}`;

// ─────────────────────────────────────────────
// DOCUMENTED ENDPOINTS
// ─────────────────────────────────────────────

// GET /family_tree — assembled tree (parents / siblings / grandparents / aunts-uncles).
export const familyTreeKey = (journalId) =>
  journalId ? `${base(journalId)}/family_tree` : null;

export const useFamilyTreeSWR = (journalId, opts = {}) =>
  useSWR(familyTreeKey(journalId), getSWR, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    ...opts,
  });

export const getFamilyTree = (journalId) => get(`${base(journalId)}/family_tree`);

// POST /family_members — add & link a person.
export const postFamilyMember = (journalId, attributes) =>
  post(`${base(journalId)}/family_members`, { data: { attributes } });

// PATCH /family_members/:id — edit person + label.
export const patchFamilyMember = (journalId, memberId, attributes) =>
  patch(`${base(journalId)}/family_members/${memberId}`, { data: { attributes } });

// DELETE /family_members/:id — unlink.
export const destroyFamilyMember = (journalId, memberId) =>
  destroy(`${base(journalId)}/family_members/${memberId}`);

// PATCH /family_members/:id/reorder — set 1-based position in the baby's list.
export const reorderFamilyMember = (journalId, memberId, position) =>
  patch(`${base(journalId)}/family_members/${memberId}/reorder`, {
    data: { attributes: { position } },
  });

// GET .../talking_family_tree/book_pages — polled while the worker builds pages.
export const bookPagesKey = (journalId) =>
  journalId ? `${base(journalId)}/talking_family_tree/book_pages` : null;

// States returned by the book_pages endpoint (per backend docs).
export const BOOK_PAGES_STATES = {
  CREATING: 'creating_pages',
  FINISHED: 'finished_creating_pages',
};

// ─────────────────────────────────────────────
// ⚠️ ASSUMED ENDPOINTS — NOT IN BACKEND DOCS
// The design requires an invitation link, video-idea "topics", and a public
// QR-linked page, but the provided backend documentation defines none of these.
// These are isolated here, behind clearly-named functions, using paths consistent
// with the documented ones. They MUST be confirmed before going live.
// See TALKING_FAMILY_TREE_PLAN.md §6 (blockers).
// ─────────────────────────────────────────────

// Read/patch the talking_family_tree resource itself (invite link, topics, message).
export const talkingFamilyTreeKey = (journalId) =>
  journalId ? `${base(journalId)}/talking_family_tree` : null;

export const useTalkingFamilyTreeSWR = (journalId, opts = {}) =>
  useSWR(talkingFamilyTreeKey(journalId), getSWR, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    ...opts,
  });

// Persist invitation config (ordered selected-topics + personal message).
// selected-topics is an ordered array of { 'topic-id' } (catalog) or
// { 'custom-description' } (owner-authored) objects.
export const patchTalkingFamilyTree = (journalId, attributes) =>
  patch(`${base(journalId)}/talking_family_tree`, {
    data: { type: 'talking-family-trees', attributes },
  });

// Public read of a shared family tree by slug (QR code / public link).
export const publicFamilyTreeKey = (slug) =>
  slug ? `/v3/public/talking_family_trees/${slug}` : null;

export const getPublicFamilyTree = (slug) =>
  get(`/v3/public/talking_family_trees/${slug}`);

// Recipient self-add via the public invite (see TFT-invitation-page.png).
// TODO(api): confirm the public submit path & attribute names.
export const postPublicFamilyMember = (slug, attributes) =>
  post(`/v3/public/talking_family_trees/${slug}/family_members`, { data: { attributes } });

// Avatar upload — mirrors the canonical app flow (uploadMemberAvatar): create a
// Medium against the generic /v2/media endpoint, then PUT the blob to its upload-url
// (see story-media/upload-medium.uploadMedium). Media is owned by the underlying Member
// (owner-type 'Member', owner-id = the relative's member id from GET /family_tree).
export const uploadFamilyMemberAvatar = (memberId, filename, attrs = {}) =>
  post('/v2/media', {
    type: 'Image',
    data: {
      attributes: {
        type: 'Image',
        'owner-type': 'Member',
        'owner-id': memberId,
        filename,
        ...attrs,
      },
    },
  });

// Video upload — same flow as the avatar, owned by the underlying Member
// (owner-type 'Member', owner-id = the relative's member id). The caller then PUTs
// the file to the returned upload-url (story-media/upload-medium.uploadMedium).
export const uploadFamilyMemberVideo = (memberId, filename, attrs = {}, relationShipKind = 'Member') =>
  post('/v2/media', {
    type: 'Video',
    data: {
      attributes: {
        type: 'Video',
        'owner-type': 'FamilyRelationshipLabel',
        'owner-id': memberId,
        filename,
        ...attrs,
      },
    },
  });

// Extract the id of the resource created by a POST (normalized responses attach
// the raw JSON:API body as `initial`).
export const createdId = (response) => response?.data?.initial?.data?.id;

// Extract the underlying Member id from a created family_member POST response
// (media is owned by the Member, not the family_member link).
export const createdMemberId = (response) => {
  const data = response?.data?.initial?.data;
  return (
    data?.attributes?.['member-id']
    || data?.attributes?.['member_id']
    || data?.relationships?.member?.data?.id
    || undefined
  );
};

export const createdLockVersionOfMedia = (response) => {
  const data = response?.data?.initial?.data;
  return (
    data?.attributes?.['lock-version']
    || 0
  );
};

export const createdRelationshipId = (response) => {
  const data = response?.data?.initial?.data;
  return (
    data?.attributes?.['label-id']
    || undefined
  );
};